import React, { type ComponentType, useMemo, useRef, useEffect, useCallback } from 'react';
import { styled } from '@compiled/react';
import { useIssueBreakdown as useIssueBreakdownNew } from '@atlassian/jira-ai-work-breakdown';
import { ff } from '@atlassian/jira-feature-flagging';
import { useChildIssuesLimit, useIsAiOptInEnabled } from '@atlassian/jira-issue-context-service';
import type { CreatedIssueWithIssueData } from '@atlassian/jira-issue-create-common-types';
import {
	GIC_LIFECYCLE_EVENTS,
	useGICLifeCycleEvents,
} from '@atlassian/jira-issue-create-extensibility';
import type { ServerAssociatedIssue } from '@atlassian/jira-issue-shared-types';

import {
	EXCEEDED_LIMIT_AFTER_LOAD,
	EXCEEDED_LIMIT_INITIAL_LOAD,
	WITHIN_LIMIT,
	type ChildIssuesLimitStatus,
	GIC_CALLBACK_PAYLOAD_INLINE_CREATE_ID,
} from '@atlassian/jira-issue-view-common-constants';
import type { ChildIssueType } from '@atlassian/jira-issue-view-common-types/src/child-issue-type';
import {
	type ChildIssue,
	getChildIssuesLimitExceededAnalyticsCount,
} from '@atlassian/jira-issue-view-common-types/src/children-issues-type';
import {
	FireScreenAnalytics,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import type { IssueKey, BaseUrl } from '@atlassian/jira-shared-types';
import transfromServerChild from '../common/transform-server-child';
import {
	hasExceededIssuesLimitInitialLoad,
	hasExceededIssuesLimitAfterLoad as hasExceededIssuesLimitAfterLoadUtil,
} from '../common/util';
import { useChildIssuesSortBy, useShouldTriggerAiSuggestedIssues } from '../hooks';
import type { ChildIssuesPanelType } from '../model/types';

import { CHILD_PANEL_VIEWS, type ChildPanelView } from '../state/ui/actions';

import {
	AiIssueBreakdown as AiIssueBreakdownOld,
	AiIssueBreakdownEntryPointContainer,
} from './ai-issue-breakdown';

import { useIssueBreakdown as useIssueBreakdownOld } from './ai-issue-breakdown/context';
import { ChildIssuesLimitFlag } from './child-issues-limit-flag';
import { ChildIssuesLimitPanel } from './child-issues-limit-panel';
import { ConfigurableChildIssuesPanel } from './configurable-child-issues-panel/src/view/ui';
import type { AddChildEventHandler } from './heading/add-child/types';
import { HideDoneIconWithToolTip } from './hide-done-icon-with-tooltip';
import { getIsSubtaskCreateEnabled, useHideDoneChildIssues } from './utils';

// Only show progress summary when there are at least two child issues (or subtasks).
const PROGRESS_SUMMARY_MIN_ISSUE_COUNT = 2;

export type OwnProps = {
	hasExceededIssuesLimitAfterLoad: boolean;
	sourceName?: string;
	childIssuesLimitUrl: string;
	totalChildIssueCount: number;
	onAddChildClick: AddChildEventHandler | undefined;
};

type ConnectProps = {
	Heading: ComponentType<{
		onAddChildClick?: AddChildEventHandler;
		sourceName?: string;
		childIssuesLimitStatus: ChildIssuesLimitStatus;
		issueHierarchyLevel: number | undefined;
	}>;
	Issues: ComponentType<{
		hasExceededChildIssuesLimitOnLoadOrAfter: boolean;
	}>;
	InlineCreate: ComponentType<{
		inputId?: string;
	}>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ProgressSummary: ComponentType<Record<any, any>>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	AddExistingIssue: ComponentType<Record<any, any>>;
	allIssues: ChildIssue[];
	addClickCount?: number;
	childPanelView: ChildPanelView;
	incompleteIssues: ChildIssue[];
	childIssuesPanelType?: ChildIssuesPanelType;
	issueHierarchyLevel?: number;
	epicLinkFieldKey?: string | null;
	parentIssueKey?: IssueKey;
	baseUrl: BaseUrl;
	childIssueTypes: ChildIssueType[];
	issueTypeWithWorkflowValidators?: { [issueTypeId: string]: boolean };
	onFullDialogCreate: (optimsticId: string, childIssue: ChildIssue) => void;
	onSuggestedChildIssueCreateSuccess: (
		createdChild: CreatedIssueWithIssueData | ChildIssue,
	) => void;
};

export type Props = OwnProps & ConnectProps;

export const ChildIssuesPanel = ({
	hasExceededIssuesLimitAfterLoad,
	totalChildIssueCount,
	addClickCount = 0,
	Heading,
	Issues,
	InlineCreate,
	ProgressSummary,
	AddExistingIssue,
	allIssues,
	childPanelView,
	sourceName,
	childIssuesLimitUrl,
	onAddChildClick,
	childIssuesPanelType,
	issueHierarchyLevel,
	epicLinkFieldKey,
	parentIssueKey,
	baseUrl,
	childIssueTypes,
	issueTypeWithWorkflowValidators,
	onFullDialogCreate,
	onSuggestedChildIssueCreateSuccess,
	incompleteIssues,
}: Props) => {
	const initialTotalChildIssueCount = useRef<number>(totalChildIssueCount);

	const childIssuesLimitStatus = useMemo(() => {
		if (childIssuesLimitUrl) {
			return EXCEEDED_LIMIT_INITIAL_LOAD;
		}

		if (hasExceededIssuesLimitAfterLoad) {
			return EXCEEDED_LIMIT_AFTER_LOAD;
		}

		return WITHIN_LIMIT;
	}, [childIssuesLimitUrl, hasExceededIssuesLimitAfterLoad]);

	const shouldTriggerAiSuggestedIssues = useShouldTriggerAiSuggestedIssues();

	const hasIssues = useMemo(() => allIssues.length > 0, [allIssues.length]);
	const hasExceededChildIssuesLimitInitialLoad = useMemo(
		() => hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus),
		[childIssuesLimitStatus],
	);

	const hasExceededChildIssuesLimitAfterLoad = useMemo(
		() => hasExceededIssuesLimitAfterLoadUtil(childIssuesLimitStatus),
		[childIssuesLimitStatus],
	);

	const hasContent =
		hasExceededChildIssuesLimitInitialLoad ||
		hasIssues ||
		addClickCount > 0 ||
		shouldTriggerAiSuggestedIssues;

	const childIssuesLimit = useChildIssuesLimit();
	const approachingLimitCutOff = childIssuesLimit - 1;

	const startedBelowTheCutoff =
		!hasExceededChildIssuesLimitInitialLoad &&
		initialTotalChildIssueCount.current < approachingLimitCutOff;

	const isNowAtTheCutoff = totalChildIssueCount === approachingLimitCutOff;

	const isApproachingLimit = startedBelowTheCutoff && isNowAtTheCutoff;

	const { shouldShowHideDoneInfoIcon, completedIssuesCount } = useHideDoneChildIssues(
		allIssues,
		incompleteIssues,
	);

	const [sortByValue] = useChildIssuesSortBy();

	const renderIssues = useMemo(() => {
		const progressSummary =
			!hasExceededChildIssuesLimitInitialLoad &&
			allIssues.length >= PROGRESS_SUMMARY_MIN_ISSUE_COUNT ? (
				<ProgressSummary />
			) : null;

		return allIssues.length > 0 ? (
			<>
				<ProgressSummaryWithHideDoneIconContainer>
					{progressSummary}
					{shouldShowHideDoneInfoIcon && (
						<HideDoneIconWithToolTip completedChildIssuesCount={completedIssuesCount} />
					)}
				</ProgressSummaryWithHideDoneIconContainer>
				{ff('configurable-child-issues-panel_3ht06') ? (
					<ConfigurableChildIssuesPanel recentlyCreatedIssue={undefined} />
				) : (
					<Issues
						hasExceededChildIssuesLimitOnLoadOrAfter={
							hasExceededChildIssuesLimitAfterLoad || hasExceededChildIssuesLimitInitialLoad
						}
					/>
				)}
			</>
		) : null;

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		shouldShowHideDoneInfoIcon,
		completedIssuesCount,
		allIssues.length,
		incompleteIssues.length,
		hasExceededChildIssuesLimitInitialLoad,
		hasExceededChildIssuesLimitAfterLoad,
		sortByValue,
	]);

	const { subscribe } = useGICLifeCycleEvents();
	const isSubtaskCreateEnabled = getIsSubtaskCreateEnabled(
		childIssuesPanelType,
		issueHierarchyLevel,
	);

	useEffect(
		() =>
			subscribe?.(GIC_LIFECYCLE_EVENTS.ISSUE_CREATE_SUCCESS, async (payload) => {
				if (payload.callbackPayload?.id !== GIC_CALLBACK_PAYLOAD_INLINE_CREATE_ID) return;
				const { createdIssueDetails } = payload.createdIssueData;

				const callbackData = payload.callbackPayload?.data;

				if (
					callbackData.parentIssueKey !== parentIssueKey ||
					callbackData.childIssuesPanelType !== childIssuesPanelType
				)
					return;

				if (
					isSubtaskCreateEnabled ||
					(epicLinkFieldKey && createdIssueDetails.fields[epicLinkFieldKey]) === parentIssueKey ||
					callbackData?.parentIssueKey === parentIssueKey
				) {
					onFullDialogCreate(
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						callbackData.optimisticId as string,
						transfromServerChild(
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							createdIssueDetails as unknown as ServerAssociatedIssue,
							baseUrl,
						),
					);
				}
			}),
		[
			subscribe,
			baseUrl,
			epicLinkFieldKey,
			onFullDialogCreate,
			parentIssueKey,
			childIssuesPanelType,
			isSubtaskCreateEnabled,
		],
	);

	const useIssueBreakdown = ff('jira-ai-work-breakdown-new-ui_z0inn')
		? useIssueBreakdownNew
		: useIssueBreakdownOld;

	const [{ currentStep }, { closeIssueBreakdown, isIssueBreakdownOpen }] = ff(
		'jira-intelligent-issue-breakdown_18syl',
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIssueBreakdown()
		: [
				{ currentStep: 'defaultStep' },
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				{ closeIssueBreakdown: () => {}, isIssueBreakdownOpen: () => false },
			];

	const isAiOptInEnabled = ff('jira-intelligent-issue-breakdown_18syl')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsAiOptInEnabled()
		: false;

	const renderCreate = useMemo(() => {
		const isAiEnabledAndOpen =
			ff('jira-intelligent-issue-breakdown_18syl') && isAiOptInEnabled && isIssueBreakdownOpen();

		// Create panel should not render if AI issue create is open
		if (isAiEnabledAndOpen) return;

		if (addClickCount > 0) {
			switch (childPanelView) {
				case CHILD_PANEL_VIEWS.InlineCreate:
					return <InlineCreate inputId={sourceName} />;
				case CHILD_PANEL_VIEWS.AddExistingIssue:
					return <AddExistingIssue />;
				default:
					return null;
			}
		}
		return null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addClickCount, childPanelView, sourceName, currentStep, isIssueBreakdownOpen]);

	const onAddChildClickWithAiBreakdownEnabled = useCallback<AddChildEventHandler>(
		(e, analyticsEvent) => {
			if (isAiOptInEnabled) closeIssueBreakdown();
			onAddChildClick && onAddChildClick(e, analyticsEvent);
		},
		[isAiOptInEnabled, closeIssueBreakdown, onAddChildClick],
	);

	const childIssuesLimitExceededAnalyticsCount =
		getChildIssuesLimitExceededAnalyticsCount(childIssuesLimit);

	const renderAIWorkBreakdown = useMemo(() => {
		if (childIssueTypes.length === 0) return null;

		// killswitch
		if (!ff('jira-intelligent-issue-breakdown_18syl')) return null;

		if (ff('jira-ai-work-breakdown-new-ui_z0inn')) {
			const childIssueTypeWithWFValidator = childIssueTypes.map((issueType) => ({
				...issueType,
				hasWorkflowValidators:
					(issueTypeWithWorkflowValidators && issueTypeWithWorkflowValidators[issueType.id]) ||
					false,
			}));
			return (
				<AiIssueBreakdownEntryPointContainer
					onCreate={onSuggestedChildIssueCreateSuccess}
					childIssueTypes={childIssueTypeWithWFValidator}
					isSubTaskCreationOpen={isSubtaskCreateEnabled}
					issueHierarchyLevel={issueHierarchyLevel}
				/>
			);
		}

		return (
			<AiIssueBreakdownOld
				onCreate={onSuggestedChildIssueCreateSuccess}
				childIssueTypes={childIssueTypes}
				isSubTaskCreationOpen={isSubtaskCreateEnabled}
				issueHierarchyLevel={issueHierarchyLevel}
			/>
		);
	}, [
		childIssueTypes,
		issueTypeWithWorkflowValidators,
		isSubtaskCreateEnabled,
		issueHierarchyLevel,
		onSuggestedChildIssueCreateSuccess,
	]);

	return hasContent ? (
		<ContextualAnalyticsData
			attributes={{
				childCount: hasExceededChildIssuesLimitInitialLoad
					? childIssuesLimitExceededAnalyticsCount
					: allIssues.length,
				isChildIssuesLimitPanelDisplayed: hasExceededChildIssuesLimitInitialLoad,
			}}
		>
			<FireScreenAnalytics actionSubjectId={sourceName} />
			<Heading
				onAddChildClick={
					ff('jira-intelligent-issue-breakdown_18syl')
						? onAddChildClickWithAiBreakdownEnabled
						: onAddChildClick
				}
				sourceName={sourceName}
				childIssuesLimitStatus={childIssuesLimitStatus}
				issueHierarchyLevel={issueHierarchyLevel}
			/>
			{hasExceededChildIssuesLimitInitialLoad && (
				<ChildIssuesLimitPanel
					hasExceededIssuesLimitInitialLoad={hasExceededChildIssuesLimitInitialLoad}
				/>
			)}
			<ChildIssuesLimitFlag
				isApproachingLimit={isApproachingLimit}
				childIssuesLimitStatus={childIssuesLimitStatus}
			/>
			{renderIssues}
			{renderCreate}
			{renderAIWorkBreakdown}
		</ContextualAnalyticsData>
	) : null;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProgressSummaryWithHideDoneIconContainer = styled.div({
	display: 'flex',
});
